/* eslint-disable require-jsdoc */
import classNames from 'classnames';
import Icon from 'app/components/partials/icon';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import Slider from 'react-slick';
import { Timer } from '../../utilities/timer';
import truncate from 'app/utilities/truncate';
import { useIdPolyfill } from '../../hooks/use-id-polyfill';
import { formatInternalLink, formatLink }  from 'app/utilities/link-helper';
import React, { forwardRef, useEffect, useRef, useState } from 'react';

const CTA_TEXT_LIMIT = 30;
const TIMER = 5000;
const LAST_ITEM_INDEX = 6;

// Follows accessibility recommendations from ARIA APG
// https://www.w3.org/WAI/ARIA/apg/patterns/carousel/
const HeroCarousel = forwardRef(({ data, pageTitle }, ref) => {
    // Current autoplay state
    const [isPlaying, setIsPlaying] = useState(true);
    // Whether autoplay was explicitly paused
    const [forcePause, setForcePause] = useState(false);

    const [activeItem, setActiveItem] = useState(0);
    const sliderRef = useRef(null);
    const dotsRef = useRef(null);
    const needsMoveFocus = useRef(null);

    const setTimerCb = () => sliderRef.current.slickNext();

    const timerRef = useRef(new Timer(setTimerCb, TIMER));

    // Pause the autoplay
    // If force pause is given, then resume will also require force
    // Use cases to satisfy accessibility:
    // 1. Pause autoplay while hovering over the slide, but do not unpause on unhover if there's focus on the slide or autoplay was explicitly paused
    // 2. Toggling pause/resume button is a force pause
    // 3. Focus on the slide will force a pause, and will not automatically unpause onBlur
    const pauseAutoplay = (force = false) => {
        setIsPlaying(false);
        if (force) {
            setForcePause(true);
        }
        timerRef.current.pause();
    };

    // Resume autoplay
    // If pause was forced, then caller needs to force resume
    const resumeAutoplay = (force = false) => {
        if (force || !forcePause) {
            setIsPlaying(true);
            setForcePause(false);
            timerRef.current.resume();
        }
    };

    const toggleAutoPlay = () => {
        if (isPlaying) {
            // Toggling is always forcing pause/resume
            pauseAutoplay(true);
        } else {
            resumeAutoplay(true);
        }
    };

    // TODO: replace with React.useId() when upgraded to react 18+
    const blockId = useIdPolyfill();

    const getSlideId = (idx) => `${blockId}-slide-${idx}`;

    // force focus on the selected tab (in case of using keyboard)
    const moveTabFocus = (idx) => {
        dotsRef.current.children.item(idx).getElementsByClassName('dot')[0].focus();
    };

    const arrowClickHandler = (type) => {
        if (type === 'prev') {
            sliderRef.current.slickPrev();
        } else {
            sliderRef.current.slickNext();
        }
    };

    const renderMediaInner = (imageSrc, imageMobileSrc, fileAlt, video, title) => {
        const config = {
            youtube: {
                playerVars: {
                    autoplay: 0, // Ensure YouTube does not autoplay
                    controls: 1,
                    loop: 1,
                    showinfo: 0,
                    mute: 1,
                }
            }
        };

        return (
            <React.Fragment>
                {video && <ReactPlayer className="video-player" url={video} width="100%" height="150%" config={config} />}
                <img className="image" src={imageSrc} alt={fileAlt || title} />
                <img className="image mobile" src={imageMobileSrc} alt={fileAlt || title} />
            </React.Fragment>
        );
    };

    const renderHero = (rowData, index) => {
        const { 'cta-text': ctaText, file: imageSrc, 'file_alt': fileAlt, 'file_mobile': imageMobileSrc, link, subtitle, 'text-color': textColor, title, video } = rowData;

        const formattedInternalLink = formatInternalLink(link);

        // accessibility: pause autoplay if links are focused and require user to explicitly restart autoplay if they need
        return (
            <div className="hero" key={index}
                id={getSlideId(index)}
                role="tabpanel"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${data.length}`}
            >
                <div className="hero-wrapper || constrain-width large no-padding"
                    // accessibility: pause autoplay on hover and resume timer when leave hover
                    onMouseOver={() => pauseAutoplay()}
                    onMouseOut={() => resumeAutoplay()}
                >
                    {formattedInternalLink ? (
                        <Link className="hero-media-container"
                            to={formattedInternalLink}
                            onFocus={() => pauseAutoplay(true)}
                        >
                            {renderMediaInner(imageSrc, imageMobileSrc, fileAlt, video, title)}
                        </Link>
                    ) : (
                        <a className="hero-media-container" href={formatLink(link)}
                            onFocus={() => pauseAutoplay(true)}
                        >
                            {renderMediaInner(imageSrc, imageMobileSrc, fileAlt, video, title)}
                        </a>
                    )}
                    <div className="hero-banner" style={{ color: textColor }}>
                        <div className="banner-info">
                            <h2 className="h3 title">{title}</h2>
                            <p className="sub-title">{subtitle}</p>
                        </div>
                        {formattedInternalLink
                            ? (<Link
                                className="button"
                                to={formattedInternalLink}
                                onFocus={() => pauseAutoplay(true)}
                            >
                                {truncate(ctaText, CTA_TEXT_LIMIT)}
                            </Link>)
                            : (<a
                                className="button"
                                href={formatLink(link)}
                                onFocus={() => pauseAutoplay(true)}
                            >{truncate(ctaText, CTA_TEXT_LIMIT)}</a>)
                        }
                    </div>
                </div>
            </div>
        );
    };

    const renderDots = () => (
        <React.Fragment>
            {data.map((ele, index) => {
                const isEleActive = index === activeItem;

                if (index > LAST_ITEM_INDEX) {
                    return;
                }

                return (
                    <li className="dot-container" key={index}>
                        <button
                            className={`dot${isEleActive ? ' is-active' : ''}`}
                            onClick={() => sliderRef.current.slickGoTo(index)}
                            tabIndex={isEleActive ? undefined : '-1'}
                            aria-selected={isEleActive}
                            aria-label={`Slide ${index + 1}`}
                            aria-controls={getSlideId(index)}
                            type="button"
                            role="tab"
                        >
                            {isEleActive ? (
                                <svg viewBox="0 0 200 200">
                                    <circle className={classNames('circle-timer', { paused: !isPlaying })} cx="100" cy="100" r="80" transform="rotate(-90 100 100)" />
                                </svg>
                            ) : <Icon name="circle" />}
                        </button>
                        <div className="tooltip">
                            <p>{ele.title}</p>
                        </div>
                    </li>
                );
            })}
        </React.Fragment>
    );

    // Start timer on slide change
    useEffect(() => {
        // Make child elements in all slides unfocusable except for the currently active slide
        ref.current.querySelectorAll('.slick-slide').forEach((slide) => {
            slide.querySelectorAll('a, button, input, textarea, select, iframe, [tabindex]').forEach((element) => {
                if (slide.dataset.index == activeItem) {
                    element.removeAttribute('tabindex');
                } else {
                    element.setAttribute('tabindex', '-1');
                }
            });
        });

        // Only start if autoplay is enabled
        if (isPlaying) {
            timerRef.current.resume();
        }

        return () => {
            timerRef.current.stop();
        };
    }, [activeItem]);

    // Sync timer with autoplay state
    useEffect(() => {
        if (isPlaying) {
            timerRef.current.resume();
        } else {
            timerRef.current.pause();
        }
    }, [isPlaying]);

    const settings = {
        arrows: false,
        dots: false,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        vertical: true,
        beforeChange: (current, next) => {
            // Cases where we need to move focus: triggering from tab navigation
            // Cases where we don't need to move focus: triggering from next/prev buttons, auto timer expire
            if (needsMoveFocus && needsMoveFocus.current) {
                moveTabFocus(next);
                needsMoveFocus.current = false;
            }
            setActiveItem(next);
        },
        responsive: [
            {
                breakpoint: 800,
                settings: {
                    vertical: false
                }
            }
        ]
    };

    return (
        <div className="content-block || hero-carousel" ref={ref}>
            {/* accessibility: announce carousel slide changes (title only to avoid being too chatty) */}
            {/* hack: add class for full-screen-dialog to turn off aria-live when active */}
            <div className="sr-only hero-carousel-aria-live" aria-live="polite">
                {data[activeItem].title}
            </div>
            <section className="hero-carousel-container"
                aria-label="Hero carousel"
            >
                <div className="hero-carousel-background">
                    <img key={activeItem} className="image" src={data[activeItem].file} alt={pageTitle}/>
                    <div className="overlay" style={{ backgroundColor: data[activeItem]['text-bg'], opacity: 0.9 }}></div>
                </div>
                <div className="paging-container">
                    <div className="paging" style={{ color: data[activeItem]['text-color'] || '#ffffff' }}>
                        <button
                            className="toggle-autoplay"
                            aria-label={forcePause ? 'Play automatic carousel' : 'Pause automatic carousel'}
                            onClick={toggleAutoPlay}
                        >
                            {forcePause &&
                                <Icon name="autoplay" />
                            }
                            {!forcePause &&
                                <Icon name="autopause" />
                            }
                        </button>
                        <button
                            className="slick-arrow slick-arrow__prev"
                            onClick={() => arrowClickHandler('prev')}
                            aria-label="Previous slide"
                        >
                            <Icon name="chevron" />
                        </button>
                        <ul className="paging-dots"
                            role="tablist"
                            aria-label="Slides controls"
                            ref={dotsRef}
                            onKeyDown={(e) => {
                                switch (e.key) {
                                    case 'ArrowRight': {
                                        needsMoveFocus.current = true;
                                        sliderRef.current.slickNext();
                                        break;
                                    }
                                    case 'ArrowLeft': {
                                        needsMoveFocus.current = true;
                                        sliderRef.current.slickPrev();
                                        break;
                                    }
                                    default:
                                }
                            }}
                        >
                            {renderDots()}
                        </ul>
                        <button
                            className="slick-arrow slick-arrow__next"
                            onClick={() => arrowClickHandler('next')}
                            aria-label="Next slide"
                        >
                            <Icon name="chevron" />
                        </button>
                    </div>
                </div>
                <Slider {...settings} ref={sliderRef}>
                    {data.map((hero, index) => {
                        if (index > LAST_ITEM_INDEX) {
                            return;
                        }

                        return renderHero(hero, index);
                    })}
                </Slider>
            </section>
        </div>
    );
});

HeroCarousel.displayName = 'HeroCarousel';

HeroCarousel.propTypes = {
    data: PropTypes.array.isRequired,
    pageTitle: PropTypes.string.isRequired
};

export default HeroCarousel;
